import { useEffect, useState } from 'react';
import { blindsTrackingService } from '../services/api';
import './css/SchucoPredictionCorrection.css';

export default function BlindsPredictionCorrection({ item, onChange }) {
  const [editing, setEditing] = useState(false);
  const [days, setDays] = useState('');
  const [validityDays, setValidityDays] = useState('7');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now);
  const expiresAt = Date.parse(item.correction_expires_at);
  const active = item.correction_days != null && expiresAt > now;

  useEffect(() => {
    if (!Number.isFinite(expiresAt)) return undefined;
    const refresh = () => setNow(Date.now());
    const timer = expiresAt > now
      ? setTimeout(refresh, Math.min(Math.max(expiresAt - Date.now(), 1), 60000))
      : undefined;
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [expiresAt, now]);

  const startEditing = () => {
    setDays(active ? String(item.correction_days) : '');
    setValidityDays('7');
    setError('');
    setEditing(true);
  };

  const save = async (event) => {
    event.preventDefault();
    const value = Number(days.replace(',', '.'));
    const duration = Number(validityDays);
    if (!days.trim() || !Number.isFinite(value) || value < 0
      || !Number.isInteger(duration) || duration < 1 || duration > 365) {
      setError('Podaj korektę w dniach (nie mniejszą niż 0) i okres ważności od 1 do 365 dni.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const correction = await blindsTrackingService.savePredictionCorrection({
        item_number: item.item_number,
        correction_days: value,
        validity_days: duration,
      });
      onChange(correction);
      setNow(Date.now());
      setEditing(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Nie udało się zapisać korekty. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setError('');
    try {
      await blindsTrackingService.deletePredictionCorrection(item.item_number);
      onChange({
        item_number: item.item_number,
        correction_days: null,
        correction_expires_at: null,
      });
      setEditing(false);
    } catch (err) {
      setError('Nie udało się usunąć korekty. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="schuco-correction">
      {editing ? (
        <form onSubmit={save}>
          <label>
            Korekta (dni)
            <input
              type="text"
              inputMode="decimal"
              value={days}
              onChange={event => setDays(event.target.value)}
              required
              disabled={saving}
              autoFocus
            />
          </label>
          <label>
            Ważność (dni kalendarzowe)
            <input
              type="number"
              min="1"
              max="365"
              step="1"
              value={validityDays}
              onChange={event => setValidityDays(event.target.value)}
              required
              disabled={saving}
            />
          </label>
          <div className="schuco-correction-actions">
            <button type="button" disabled={saving} onClick={() => setValidityDays('3')}>3 dni</button>
            <button type="button" disabled={saving} onClick={() => setValidityDays('7')}>Tydzień</button>
          </div>
          <small>Ważność liczona od zapisu.</small>
          <div className="schuco-correction-actions">
            <button type="submit" disabled={saving}>{saving ? 'Zapisywanie…' : 'Zapisz'}</button>
            <button type="button" disabled={saving} onClick={() => { setEditing(false); setError(''); }}>Anuluj</button>
          </div>
        </form>
      ) : (
        <>
          <strong>{active ? item.correction_days : '—'}</strong>
          {active && <small>Do {new Date(expiresAt).toLocaleString('pl-PL')}</small>}
          <div className="schuco-correction-actions">
            <button type="button" disabled={saving} onClick={startEditing} aria-label={`Edytuj korektę ${item.item_number}`}>
              {active ? 'Edytuj' : 'Dodaj'}
            </button>
            {active && <button type="button" disabled={saving} onClick={remove}>Usuń</button>}
          </div>
        </>
      )}
      {error && <small className="schuco-correction-error" role="alert">{error}</small>}
    </div>
  );
}
